/**
 * React Query v5 hooks for B1.9 InvoiceList.
 *
 * Cache key family:
 *   ['invoice-list', branchId, filters] → InvoiceListSnapshot
 *
 * Mutations (`issue`, `send`, `void`) invalidate the list cache for the branch.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryKey,
  type UseMutationOptions,
} from '@tanstack/react-query';

import {
  downloadInvoice,
  issueInvoice,
  listInvoices,
  sendInvoice,
  voidInvoice,
} from './invoice-api';
import type {
  DownloadInvoiceRequest,
  InvoiceListFilters,
  InvoiceListSnapshot,
  InvoiceRow,
  IssueInvoiceRequest,
  SendInvoiceRequest,
  VoidInvoiceRequest,
} from './types';

export function invoiceListKey(
  branchId: string | undefined,
  filters: Partial<InvoiceListFilters> | undefined,
): QueryKey {
  return ['invoice-list', branchId ?? 'pending', filters ?? {}];
}

export function useInvoiceList(
  branchId: string | undefined,
  filters: Partial<InvoiceListFilters> | undefined,
) {
  return useQuery<InvoiceListSnapshot>({
    queryKey: invoiceListKey(branchId, filters),
    queryFn: () =>
      listInvoices({ branchId: branchId ?? '', filters: filters ?? {} }),
    enabled: Boolean(branchId),
    staleTime: 20_000,
  });
}

function useInvoiceMutation<TRequest>(
  branchId: string | undefined,
  fn: (req: TRequest) => Promise<InvoiceRow>,
  options?: UseMutationOptions<InvoiceRow, Error, TRequest>,
) {
  const qc = useQueryClient();
  return useMutation<InvoiceRow, Error, TRequest>({
    mutationFn: fn,
    onSuccess: (row, ...rest) => {
      qc.invalidateQueries({ queryKey: ['invoice-list', branchId ?? 'pending'] });
      options?.onSuccess?.(row, ...rest);
    },
    ...options,
  });
}

export function useIssueInvoice(branchId: string | undefined) {
  return useInvoiceMutation<IssueInvoiceRequest>(branchId, issueInvoice);
}

export function useSendInvoice(branchId: string | undefined) {
  return useInvoiceMutation<SendInvoiceRequest>(branchId, sendInvoice);
}

export function useVoidInvoice(branchId: string | undefined) {
  return useInvoiceMutation<VoidInvoiceRequest>(branchId, voidInvoice);
}

export function useDownloadInvoice() {
  return useMutation<
    { url: string; filename: string },
    Error,
    DownloadInvoiceRequest
  >({
    mutationFn: downloadInvoice,
  });
}
